import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAi, useAlarms, useCockpit, useHang, useLabEntries, useProduction } from '@/lib/query';
import { fmt } from '@/lib/format';
import { Badge, Card, EmptyHint, MetricCard, StatusPill } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';
import { WIDGET_LABELS, loadPrefs, move, savePrefs, toggleHidden, type DashboardPrefs } from '@/lib/dashboardPrefs';

function useDashboardPrefs() {
  const [prefs, setPrefs] = useState<DashboardPrefs>(() => loadPrefs(window.localStorage));
  useEffect(() => savePrefs(prefs, window.localStorage), [prefs]);
  return { prefs, setPrefs };
}

export default function Dashboard() {
  const { prefs, setPrefs } = useDashboardPrefs();
  const [edit, setEdit] = useState(false);
  const visible = prefs.order.filter((id) => !prefs.hidden.includes(id));

  return (
    <div>
      <PageBanner />
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-[22px] font-bold text-slate-900">自定义看板</h2>
          <p className="mt-0.5 text-[13px] text-slate-500">勾选要展示的模块、上下移排序，配置保存在本机。</p>
        </div>
        <button onClick={() => setEdit((e) => !e)} className={edit ? 'btn btn-dark' : 'btn btn-ghost'}>{edit ? '完成' : '编辑看板'}</button>
      </div>

      {edit ? <EditBar prefs={prefs} setPrefs={setPrefs} /> : null}

      {visible.length === 0 ? (
        <Card className="p-6"><EmptyHint>没有显示的模块，点右上「编辑看板」勾选。</EmptyHint></Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {visible.map((id) => (
            <Widget key={id} id={id} edit={edit} setPrefs={setPrefs} />
          ))}
        </div>
      )}
    </div>
  );
}

function EditBar({ prefs, setPrefs }: { prefs: DashboardPrefs; setPrefs: (fn: (p: DashboardPrefs) => DashboardPrefs) => void }) {
  return (
    <Card className="mb-4 p-4">
      <div className="flex flex-wrap gap-2">
        {prefs.order.map((id) => {
          const hidden = prefs.hidden.includes(id);
          return (
            <button
              key={id}
              onClick={() => setPrefs((p) => toggleHidden(p, id))}
              className={hidden ? 'rounded-lg border border-hairline px-3 py-1.5 text-[13px] text-slate-400' : 'rounded-lg border border-brand-500 bg-brand-50 px-3 py-1.5 text-[13px] font-medium text-brand-700'}
            >
              {hidden ? '○ ' : '● '}
              {WIDGET_LABELS[id] ?? id}
            </button>
          );
        })}
      </div>
      <div className="mt-2 text-[12px] text-slate-400">点模块名切换显示；用每张卡上的 ↑ ↓ 调整顺序。</div>
    </Card>
  );
}

function Widget({ id, edit, setPrefs }: { id: string; edit: boolean; setPrefs: (fn: (p: DashboardPrefs) => DashboardPrefs) => void }) {
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[14px] font-semibold text-slate-800">{WIDGET_LABELS[id] ?? id}</span>
        {edit ? (
          <span className="flex gap-1">
            <button onClick={() => setPrefs((p) => move(p, id, -1))} className="rounded border border-hairline px-2 text-[13px] text-slate-500 hover:bg-slate-50" aria-label="上移">↑</button>
            <button onClick={() => setPrefs((p) => move(p, id, 1))} className="rounded border border-hairline px-2 text-[13px] text-slate-500 hover:bg-slate-50" aria-label="下移">↓</button>
          </span>
        ) : (
          <Link to={WIDGET_ROUTE[id] ?? '/'} className="text-[12px] text-brand-600 hover:underline">进入 →</Link>
        )}
      </div>
      <WidgetBody id={id} />
    </Card>
  );
}

const WIDGET_ROUTE: Record<string, string> = { cockpit: '/', alarms: '/alarms', hang: '/hang', lab: '/lab', production: '/production', ai: '/ai' };

function WidgetBody({ id }: { id: string }) {
  const { data: cockpit } = useCockpit();
  const { data: alarms } = useAlarms();
  const { data: hang } = useHang();
  const { data: lab } = useLabEntries();
  const { data: prod } = useProduction();
  const { data: ai } = useAi();

  if (id === 'cockpit') {
    return (
      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="活动告警" value={alarms?.active.length ?? '—'} tone={alarms?.active.length ? 'alarm' : 'default'} />
        <MetricCard label="存塘批次" value={prod?.batches.length ?? '—'} />
        <MetricCard label="补水塔" value={fmt(cockpit?.makeup.bufferTowerPct, 0)} unit="%" />
      </div>
    );
  }
  if (id === 'alarms') {
    const a = alarms?.active ?? [];
    return a.length ? (
      <ul className="space-y-1 text-[13px]">
        {a.slice(0, 3).map((x) => (
          <li key={x.id} className="flex items-center justify-between gap-2">
            <span className="truncate text-slate-600">{x.system} · {x.message}</span>
            <Badge className="bg-alarm-bg text-alarm-fg border-alarm-border">{x.level}</Badge>
          </li>
        ))}
        {a.length > 3 ? <li className="text-[12px] text-slate-400">还有 {a.length - 3} 条…</li> : null}
      </ul>
    ) : <EmptyHint>暂无活动告警</EmptyHint>;
  }
  if (id === 'hang') {
    const u = hang ?? [];
    return u.length ? (
      <ul className="grid grid-cols-2 gap-2 text-[13px]">
        {u.slice(0, 4).map((x) => (
          <li key={x.id} className="flex items-center justify-between rounded-lg border border-hairline px-2 py-1.5">
            <span className="text-slate-600">{x.name}</span>
            <span className="flex items-center gap-2 tabular-nums"><StatusPill health={x.health} />{fmt(x.do, 1)}</span>
          </li>
        ))}
      </ul>
    ) : <EmptyHint>采集中…</EmptyHint>;
  }
  if (id === 'lab') {
    const e = lab?.effective ?? [];
    const latest = e[0];
    return latest ? (
      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="pH" value={fmt(latest.ph, 2)} />
        <MetricCard label="氨氮" value={fmt(latest.nh3n, 2)} unit="mg/L" />
        <MetricCard label="亚盐" value={fmt(latest.no2, 2)} unit="mg/L" />
      </div>
    ) : <EmptyHint>暂无化验</EmptyHint>;
  }
  if (id === 'production') {
    const s = prod?.stats;
    return (
      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="现存生物量" value={int(s?.standingBiomassKg ?? 0)} unit="kg" tone="brand" />
        <MetricCard label="平均存活" value={s?.avgSurvivalPct != null ? `${s.avgSurvivalPct}%` : '—'} />
        <MetricCard label="平均FCE" value={s?.avgFcePct != null ? `${s.avgFcePct}%` : '—'} />
      </div>
    );
  }
  if (id === 'ai') {
    return (
      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="建议投喂" value={ai?.feedSuggestKgPerMeal ?? '—'} unit="kg/餐" />
        <MetricCard label="置信(校准)" value={`${ai?.calibratedConfidencePct ?? ai?.modelConfidencePct ?? '—'}%`} tone="brand" />
        <MetricCard label="DO异常风险" value={`${ai?.anomalyRiskPct ?? 0}%`} tone={(ai?.anomalyRiskPct ?? 0) > 60 ? 'alarm' : 'default'} />
      </div>
    );
  }
  return null;
}

function int(n: number): string {
  return Math.round(n).toLocaleString('zh-CN');
}
